import React, { useState, useEffect } from 'react';
import { useNavigate, generatePath  } from 'react-router-dom';
import { Card, Button, Form, Alert, Spinner, Table, Badge } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const QuizAttemptsList = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [studyCircles, setStudyCircles] = useState([]);
    const [folders, setFolders] = useState([]);
    const [quizzes, setQuizzes] = useState([]);
    const [attempts, setAttempts] = useState([]);
    const [selectedStudyCircle, setSelectedStudyCircle] = useState('');
    const [selectedFolder, setSelectedFolder] = useState('');
    const [selectedQuiz, setSelectedQuiz] = useState('');
    const [stats, setStats] = useState(null);

    useEffect(() => {
        if (user.role !== 'admin') {
            navigate('/');
            return;
        }
        fetchStudyCircles();
    }, []);

    useEffect(() => {
        if (selectedStudyCircle) {
            fetchFolders();
        }
    }, [selectedStudyCircle]);

    useEffect(() => {
        if (selectedFolder) {
            fetchQuizzes();
        }
    }, [selectedFolder]);

    useEffect(() => {
        if (selectedQuiz) {
            fetchAttempts();
        }
    }, [selectedQuiz]);

    const fetchStudyCircles = async () => {
        try {
            const response = await api.get('/api/study-circles');
            setStudyCircles(response.data);
            setLoading(false);
        } catch (err) {
            setError('Failed to fetch study circles');
            console.error('Error fetching study circles:', err);
            setLoading(false);
        }
    };

    const fetchFolders = async () => {
        try {
            const response = await api.get(`/api/study-circles/${selectedStudyCircle}/folders`);
            setFolders(response.data);
            setSelectedFolder('');
            setSelectedQuiz('');
            setAttempts([]);
            setStats(null);
        } catch (err) {
            setError('Failed to fetch folders');
            console.error('Error fetching folders:', err);
        }
    };

    const fetchQuizzes = async () => {
        try {
            const response = await api.get(`/api/quizzes/folder/${selectedFolder}`);
            setQuizzes(response.data);
            setSelectedQuiz('');
            setAttempts([]);
            setStats(null);
        } catch (err) {
            setError('Failed to fetch quizzes');
            console.error('Error fetching quizzes:', err);
        }
    };

    const fetchAttempts = async () => {
        try {
            const response = await api.get(`/api/quizzes/scores?quiz_id=${selectedQuiz}`);
            setAttempts(response.data.attempts);
            setStats(response.data.statistics);
        } catch (err) {
            setError('Failed to fetch attempts');
            console.error('Error fetching attempts:', err);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    const openResultsInNewTab = (attemptId) => {
        const path = generatePath('/quiz/:id/results', { id: attemptId });
        window.open('/#' + path, '_blank');
    };

    if (loading) {
        return <div className="text-center mt-5"><Spinner animation="border" /></div>;
    }

    if (error) {
        return <Alert variant="danger">{error}</Alert>;
    }

    return (
        <div className="container mt-4">
            <h2 className="mb-4">Quiz Review</h2>

            <Card className="mb-4">
                <Card.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Study Circle</Form.Label>
                            <Form.Select
                                value={selectedStudyCircle}
                                onChange={(e) => setSelectedStudyCircle(e.target.value)}
                            >
                                <option value="">Select Study Circle</option>
                                {studyCircles.map(circle => (
                                    <option key={circle.id} value={circle.id}>
                                        {circle.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        {selectedStudyCircle && (
                            <Form.Group className="mb-3">
                                <Form.Label>Folder</Form.Label>
                                <Form.Select
                                    value={selectedFolder}
                                    onChange={(e) => setSelectedFolder(e.target.value)}
                                >
                                    <option value="">Select Folder</option>
                                    {folders.map(folder => (
                                        <option key={folder.id} value={folder.id}>
                                            {folder.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        )}

                        {selectedFolder && (
                            <Form.Group className="mb-3">
                                <Form.Label>Quiz</Form.Label>
                                <Form.Select
                                    value={selectedQuiz}
                                    onChange={(e) => setSelectedQuiz(e.target.value)}
                                >
                                    <option value="">Select Quiz</option>
                                    {quizzes.map(quiz => (
                                        <option key={quiz.id} value={quiz.id}>
                                            {quiz.title}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        )}
                    </Form>
                </Card.Body>
            </Card>

            {selectedQuiz && stats && (
                <Card className="mb-4">
                    <Card.Header>
                        <h4>Quiz Statistics</h4>
                    </Card.Header>
                    <Card.Body>
                        <div className="row">
                            <div className="col-md-3">
                                <div className="card bg-light">
                                    <div className="card-body text-center">
                                        <h5>Total Attempts</h5>
                                        <h3>{stats?.total_attempts || 0}</h3>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card bg-light">
                                    <div className="card-body text-center">
                                        <h5>Average Score</h5>
                                        <h3>{(stats?.average_score || 0).toFixed(1)}%</h3>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card bg-light">
                                    <div className="card-body text-center">
                                        <h5>Highest Score</h5>
                                        <h3>{(stats?.highest_score || 0).toFixed(1)}%</h3>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card bg-light">
                                    <div className="card-body text-center">
                                        <h5>Lowest Score</h5>
                                        <h3>{(stats?.lowest_score || 0).toFixed(1)}%</h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {selectedQuiz && attempts.length > 0 && (
                <Card>
                    <Card.Header>
                        <h4>Attempt Details</h4>
                    </Card.Header>
                    <Card.Body>
                        <Table striped bordered hover responsive>
                            <thead>
                                <tr>
                                    <th>Student</th>
                                    <th>Started At</th>
                                    <th>Completed At</th>
                                    <th>Score</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {attempts.map(attempt => (
                                    <tr key={attempt.id}>
                                        <td>{attempt.username}</td>
                                        <td>{formatDate(attempt.started_at)}</td>
                                        <td>{attempt.completed_at ? formatDate(attempt.completed_at) : '-'}</td>
                                        <td>{attempt.score?.toFixed(1) || '0.0'}%</td>
                                        <td>
                                            <Badge bg={attempt.status === 'completed' ? 'success' : 'warning'}>
                                                {attempt.status}
                                            </Badge>
                                        </td>
                                        <td>
                                            {attempt.status === 'completed' && (
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={() => openResultsInNewTab(attempt.id)}
                                                >
                                                    View Results
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            )}

            {selectedQuiz && attempts.length === 0 && (
                <Alert variant="info">
                    No attempts have been made for this quiz yet.
                </Alert>
            )}
        </div>
    );
};

export default QuizAttemptsList; 